import type { Theme } from "@mui/material/styles";
import { makeStyles as tssMakeStyles } from "tss-react/mui";

type CssObject = Record<string, unknown>;
type StyleEntry<P> = CssObject | ((props: P) => CssObject);
type Styles<P> = Record<string, StyleEntry<P>>;
type StylesFactory<T, P> = ((theme: T) => Styles<P>) | Styles<P>;

function resolveValue<P>(value: unknown, params: P): unknown {
  if (typeof value === "function") {
    return resolveValue((value as (p: P) => unknown)(params), params);
  }
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(value)) {
      out[k] = resolveValue((value as Record<string, unknown>)[k], params);
    }
    return out;
  }
  return value;
}

function resolve<P>(input: StylesFactory<Theme, P>, theme: Theme, params: P) {
  const styles = typeof input === "function" ? input(theme) : input;
  return resolveValue(styles, params) as Record<string, CssObject>;
}

export function makeStyles<T = Theme, P = void>(
  input: StylesFactory<T, P>
): (props?: P) => Record<string, string> {
  const useStyles = tssMakeStyles<P>()(((theme: Theme, params: P) =>
    resolve(
      input as unknown as StylesFactory<Theme, P>,
      theme,
      params
    )) as never);
  return (props?: P) => useStyles(props as P).classes;
}

export default makeStyles;
